import cn from 'classnames';
import React from 'react';
import { match, P } from 'ts-pattern';

interface LoginButtonV2Props {
  iconSrc?: string;
  icon?: React.ReactNode;
  onClick: React.MouseEventHandler;
  /** Draw the gate's 2px outline instead of the log-in modal's 1px default. */
  nonna?: boolean;
}

/**
 * `border-solid` is load-bearing: a legacy `border: 0` rule beats Tailwind's preflight on some
 * elements, so a width utility alone can compute to a 0px border.
 */
const BUTTON = (nonna: boolean) =>
  cn(
    'flex h-14 w-full items-center justify-center gap-2 rounded-lg bg-transparent px-5',
    'border-solid text-base font-normal text-eggplant',
    {
      'border-2 border-eggplant-200': nonna,
      'border border-neutral-800': !nonna,
    }
  );

/** Explicit `type='button'`: a bare `<button>` defaults to submit and would post the enclosing form. */
export const LoginButtonV2: React.FC<LoginButtonV2Props & React.ComponentPropsWithoutRef<'button'>> = ({
  icon,
  iconSrc,
  onClick,
  children,
  nonna = false,
  ...otherProps
}) => {
  const iconElement: React.ReactNode = match({ icon, iconSrc })
    .with({ icon: P.not(P.nullish) }, ({ icon }) => icon)
    .with({ iconSrc: P.not(P.nullish) }, ({ iconSrc }) => <img className='h-4' src={iconSrc} />)
    .otherwise(() => null);

  return (
    <button type='button' className={BUTTON(nonna)} onClick={onClick} {...otherProps}>
      {iconElement}
      {children}
    </button>
  );
};
